/**
 * Project-level NEW Google Drive Folders Creation
 * Light QA Contract: Project-specific, flag-controlled, real-time progress
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { FolderPlus, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
// ✅ RETIRED 2026-05-01 (PR-F2.5a): globalFeatureFlags import removed (ENABLE_NEW_GDRIVE_FOLDERS hardcoded true)
import { Progress } from '@/components/ui/progress';
import { useQueryClient } from '@tanstack/react-query';

interface FolderCreationResponse {
  success: boolean;
  projectId: string;
  projectName: string;
  prefix: string;
  foldersCreated: Record<string, { id: string; name: string }>;
  totalCreated: number;
  executionTime: string;
  tracingId: string;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

interface CreateProjectGDriveFoldersDialogProps {
  project: {
    id: string;
    project_name: string;
    new_project_folder?: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
}

export function CreateProjectGDriveFoldersDialog({ 
  project, 
  isOpen, 
  onClose 
}: CreateProjectGDriveFoldersDialogProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState<FolderCreationResponse | null>(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const queryClient = useQueryClient();

  // ✅ RETIRED 2026-05-01 (PR-F2.5a): Flag hardcoded true, backend env var preserved as safety gate

  const executeCreateFolders = async () => {
    setShowWarningDialog(false);
    setIsCreating(true);
    setProgress(0);
    setResult(null);
    setStatusMessage('Initializing...');

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 10, 90));
      }, 1500);

      setStatusMessage('Authenticating with Google Drive...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setStatusMessage('Creating project folder structure...');
      
      // Call edge function
      const { data, error } = await supabase.functions.invoke('create-new-project-folders', {
        body: { projectId: project.id }
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        // Parse backend error response from FunctionsHttpError
        let backendError: { code: string; message: string; tracingId?: string } | null = null;
        
        // Try to extract error details from the response context
        try {
          let errorBody = error.context;
          
          // If context is a JSON string, parse it first
          if (typeof errorBody === 'string') {
            errorBody = JSON.parse(errorBody);
          }
          
          // Now check for error structure in parsed body
          if (errorBody && typeof errorBody === 'object') {
            const parsed = errorBody as any;
            if (parsed.error?.code) {
              backendError = {
                code: parsed.error.code,
                message: parsed.error.message || error.message,
                tracingId: parsed.tracingId
              };
            }
          }
        } catch (parseError) {
          console.warn('[GDrive] Could not parse error context:', parseError);
        }
        
        // If we successfully parsed backend error, throw it with details
        if (backendError) {
          const detailedError = new Error(backendError.message);
          (detailedError as any).code = backendError.code;
          (detailedError as any).tracingId = backendError.tracingId;
          throw detailedError;
        }
        
        // Otherwise throw generic error
        throw new Error(error.message || 'Unknown error occurred');
      }

      if (!data) {
        throw new Error('No response from server');
      }

      setResult(data);
      setStatusMessage('Completed!');

      if (data.success) {
        toast.success('Folders created successfully!', {
          description: `Created ${data.totalCreated} folders in ${data.executionTime}`
        });
        
        // Invalidate queries to refresh project data and show new folder icons
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });
      } else {
        throw new Error(data.error?.message || 'Operation failed');
      }

    } catch (error: any) {
      console.error('Folder creation error:', error);
      setProgress(0);
      setStatusMessage('');
      
      const errorCode = error.code || 'UNKNOWN_ERROR';
      const errorMessage = error.message || 'Failed to create folders';
      const tracingId = error.tracingId;
      
      // Handle specific error codes from backend
      switch (errorCode) {
        case 'FEATURE_DISABLED':
          toast.error('Backend feature is disabled', {
            description: 'FLAG_ENABLE_NEW_GDRIVE_FOLDERS is OFF in the edge function environment. Please enable it in Supabase secrets.'
          });
          break;
        
        case 'ALREADY_EXISTS':
          toast.warning('Folders already exist', {
            description: 'Dự án này đã có folders. Nếu thiếu subfolder, vui lòng liên hệ admin để kiểm tra và sửa chữa thủ công.'
          });
          break;
        
        case 'PERMISSION_ERROR':
          toast.error('Permission denied', {
            description: 'Service account lacks access to the root folder. Please share the folder with the service account.'
          });
          break;
        
        case 'INVALID_INPUT':
          toast.error('Invalid request', {
            description: errorMessage
          });
          break;
        
        default:
          toast.error('Failed to create folders', {
            description: errorMessage
          });
      }

      setResult({
        success: false,
        projectId: project.id,
        projectName: project.project_name,
        prefix: '',
        foldersCreated: {},
        totalCreated: 0,
        executionTime: '0ms',
        tracingId: tracingId || 'N/A',
        error: {
          code: errorCode,
          message: errorMessage
        }
      } as FolderCreationResponse);

    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFolders = async () => {
    // Pre-creation warning if folders already exist
    if (project.new_project_folder) {
      setShowWarningDialog(true);
      return;
    }

    // If no warning needed, execute directly
    await executeCreateFolders();
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
      // Reset state after a short delay to prevent flashing
      setTimeout(() => {
        setProgress(0);
        setStatusMessage('');
        setResult(null);
      }, 300);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create NEW Google Drive Folders</DialogTitle>
          <DialogDescription>
            Project: <strong>{project.project_name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Project Info */}
          <div className="rounded-lg border bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">
              This will create a new automated folder structure for this project using the NEW service account.
            </p>
          </div>

          {/* Progress Bar */}
          {isCreating && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{statusMessage}</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className={`rounded-lg border p-4 space-y-2 ${
              result.success 
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
            }`}>
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1 space-y-1">
                  <p className="font-medium text-sm">
                    {result.success ? 'Success!' : 'Error'}
                  </p>
                  {result.success && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Created {result.totalCreated} folders for <strong>{result.projectName}</strong>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Execution time: {result.executionTime}
                      </p>
                      <details className="text-xs mt-2">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          View created folders
                        </summary>
                        <ul className="mt-2 space-y-1 pl-4">
                          {Object.entries(result.foldersCreated).map(([key, folder]) => (
                            <li key={key} className="font-mono">
                              {folder.name}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </>
                  )}
                  {!result.success && result.error && (
                    <>
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {result.error.message}
                      </p>
                      <div className="space-y-0.5">
                        {result.error.code && (
                          <p className="text-xs text-muted-foreground font-mono">
                            Error code: {result.error.code}
                          </p>
                        )}
                        {result.tracingId && result.tracingId !== 'N/A' && (
                          <p className="text-xs text-muted-foreground font-mono">
                            Trace ID: {result.tracingId}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ✅ RETIRED 2026-05-01 (PR-F2.5a): Flag warning removed (hardcoded true) */}
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCreating}
          >
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleCreateFolders}
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Create Folders
                </>
              )}
            </Button>
        )}
      </DialogFooter>
    </DialogContent>

    {/* Warning AlertDialog for existing folders */}
    <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Cảnh báo
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3 text-left">
            <p className="font-medium text-foreground">
              Dự án này đã có NEW GDrive folders.
            </p>
            <div className="space-y-2 text-sm">
              <p className="flex items-start gap-2">
                <span className="text-red-600 font-bold">❌</span>
                <span>Nếu một số thư mục con đã bị xóa, nhấn OK sẽ <strong>KHÔNG</strong> tạo lại chúng.</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-blue-600">👉</span>
                <span>Bạn có thể cần liên hệ admin để sửa chữa thủ công.</span>
              </p>
            </div>
            <p className="text-foreground font-medium">
              ❓ Bạn có muốn tiếp tục không?
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isCreating}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={executeCreateFolders}
            disabled={isCreating}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </Dialog>
  );
}
